// core/lib/postFieldErrors.ts — humanizes the raw field-error codes the server
// sends back in a 400 `{code:'validation', fields:{...}}` body for POST /posts/,
// so the composer never shows a bare code like "required" to a member.
// Mirrors mobile core/lib/postFieldErrors.ts — keep the copy identical.

const FIELD_ERROR_COPY: Record<string, string> = {
  required: 'Pick at least one option.',
  audience_mismatch: "That audience selection doesn't match what you picked.",
  images_not_allowed: "A poll can't be combined with images.",
  option_text: 'Poll options need 1–80 characters each.',
  duration: 'Pick a valid poll duration.',
  must_be_future: 'Pick a time in the future.',
  too_far: 'Schedule within the next 30 days.',
  // 1.24.00 C2: the server caps a thread at 100 posts including the root. The
  // composer cannot pre-empt this one — only the server knows the count — so it
  // is the one thread refusal a member can actually reach, and the generic
  // fallback would leave them re-pressing Post with no idea what to change.
  thread_full: "This thread is full. It's capped at 100 posts. Post this on its own instead.",
};

const GENERIC_FALLBACK = "Something about your post wasn't valid. Please check your selections.";

/** Humanize a single raw field-error code. Unknown codes get a generic fallback. */
export function humanizeFieldError(code: string): string {
  return FIELD_ERROR_COPY[code] ?? GENERIC_FALLBACK;
}

/**
 * Humanize the first message found across a 400's `fields` object. Each field's
 * value may be a single code string or an array of them (DRF's usual shape) —
 * either way, the FIRST one found is what surfaces to the member.
 */
export function humanizeFirstFieldError(fields: Record<string, unknown>): string {
  for (const value of Object.values(fields)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === 'string' && first.length > 0) return humanizeFieldError(first);
  }
  return GENERIC_FALLBACK;
}

/**
 * Extract an '@handle' the server named as unknown, from anywhere in a 400's
 * `fields` object (values may be strings or arrays of strings). Returns null
 * when no field mentions a handle.
 */
export function extractBadHandle(fields: Record<string, unknown>): string | null {
  for (const value of Object.values(fields)) {
    const items = Array.isArray(value) ? value : [value];
    for (const item of items) {
      if (typeof item !== 'string') continue;
      const m = /@[\w.]+/.exec(item);
      if (m) return m[0];
    }
  }
  return null;
}
